import React, { useState, useCallback } from 'react';
import { useNotionDataApi } from './useNotionDataApi';
import { useTableFormatter } from './useTableFormatter';
import { useDatabasesQuery } from '../../hooks/useDatabasesQuery';
import CustomSelect from '../CustomSelect/CustomSelect';
import EmptyTable from './EmptyTable';
import ErrorTable from './ErrorTable';
import LoadingTable from './LoadingTable';
import TableExpensive from './TableExpensive';
import './NotionDataTable.css';

const NotionDataTable = () => {
  const { getNotionData } = useNotionDataApi();
  const { formatValue } = useTableFormatter();
  const { data: databases = [], isLoading: loadingDatabases, error: databasesError } = useDatabasesQuery();
  const [data, setData] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDatabaseId, setSelectedDatabaseId] = useState('');

  const fetchData = useCallback(async (databaseId) => {
    if (!databaseId) return;
    
    try {
      setLoading(true);
      setError(null);
      setData([]);
      setProperties([]);

      const response = await getNotionData(databaseId);

      if (response.success) {
        setData(response.data);
        setProperties(response.properties);
      } else {
        setError(response.message || 'Ошибка загрузки данных');
      }
    } catch (err) {
      console.error('Error fetching Notion data:', err);
      setError(err.response?.data?.message || 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, [getNotionData]);

  const handleDatabaseChange = (e) => {
    const selectedName = e.target.value;
    const selectedDb = databases.find(db => db.name === selectedName);
    if (selectedDb) {
      setSelectedDatabaseId(selectedDb.id);
      // Load data when database is selected
      fetchData(selectedDb.id);
    }
  };

  const getDatabaseDisplayValue = () => {
    if (!selectedDatabaseId) return '';
    const db = databases.find(db => db.id === selectedDatabaseId);
    return db ? db.name : '';
  };

  return (
    <div className="notion-data-container">
      <div className="notion-data-header">
        <h2>Таблицы выплат</h2>
        <div className="database-select-wrapper">
          <label className="database-select-label">База данных:</label>
          <CustomSelect
            name="databaseId"
            value={getDatabaseDisplayValue()}
            onChange={handleDatabaseChange}
            options={Array.isArray(databases) ? databases : []}
            placeholder={loadingDatabases ? 'Загрузка...' : databasesError ? 'Ошибка загрузки' : 'Выберите базу данных'}
            disabled={loadingDatabases || !!databasesError}
            required
          />
        </div>
        {data.length > 0 && (
          <div className="data-info">
            Всего записей: <strong>{data.length}</strong>
          </div>
        )}
      </div>

      {loading && <LoadingTable />}
      {error && <ErrorTable error={error} onRetry={() => fetchData(selectedDatabaseId)} />}
      {!loading && !error && data.length === 0 && selectedDatabaseId && <EmptyTable />}
      {!loading && !error && data.length > 0 && (
        <TableExpensive data={data} properties={properties} formatValue={formatValue} />
      )}
      {!loading && !error && !selectedDatabaseId && databases.length > 0 && (
        <div className="empty-state">
          <p>Выберите базу данных для отображения таблицы</p>
        </div>
      )}
      {databasesError && (
        <div className="empty-state">
          <p>Ошибка загрузки баз данных: {databasesError.message}</p>
        </div>
      )}
      {!loadingDatabases && !databasesError && databases.length === 0 && (
        <div className="empty-state">
          <p>Базы данных не найдены</p>
        </div>
      )}
    </div>
  );
};

export default NotionDataTable;

